import { CartLinesUpdateEvent } from '@shopify/events';

class FabricBundleUpsell extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.addEventListener('change', this.handleVariantChange);
    this.addEventListener('click', this.handleClick);

    this.track = this.querySelector(
      '[data-upsell-track], .fabric-bundle__products'
    );

    this.track?.addEventListener('pointerdown', this.handleDragStart);
    this.track?.addEventListener('dragstart', this.preventNativeDrag);
    this.track?.addEventListener('click', this.preventDragClick, true);
    this.track?.addEventListener('scroll', this.handleScroll, {
      passive: true,
    });

    window.addEventListener('resize', this.handleResize);
    requestAnimationFrame(() => this.updateNavigation());
  }

  disconnectedCallback() {
    this.stopDragging?.();
    this.track?.removeEventListener('pointerdown', this.handleDragStart);
    this.track?.removeEventListener('dragstart', this.preventNativeDrag);
    this.track?.removeEventListener('click', this.preventDragClick, true);
    this.track?.removeEventListener('scroll', this.handleScroll);

    this.removeEventListener('change', this.handleVariantChange);
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('resize', this.handleResize);

    delete this.dataset.initialized;
  }

  handleDragStart = (event) => {
    this.suppressDragClick = false;

    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('button, select, input, textarea, label')) return;

    const track = this.track;
    if (!track || track.scrollWidth <= track.clientWidth) return;

    this.stopDragging?.();

    const startX = event.clientX;
    const startY = event.clientY;
    const startScroll = track.scrollLeft;
    const controller = new AbortController();
    const options = { signal: controller.signal };

    const savedStyles = [
      'scroll-behavior',
      'scroll-snap-type',
      'user-select',
      'cursor',
    ].map((key) => [
      key,
      track.style.getPropertyValue(key),
      track.style.getPropertyPriority(key),
    ]);

    track.style.setProperty('user-select', 'none');

    this.stopDragging = () => {
      controller.abort();

      for (const [key, value, priority] of savedStyles) {
        if (value) {
          track.style.setProperty(key, value, priority);
        } else {
          track.style.removeProperty(key);
        }
      }

      this.stopDragging = null;
    };

    window.addEventListener(
      'pointermove',
      (move) => {
        if (move.pointerId !== event.pointerId) return;

        if (!(move.buttons & 1)) {
          this.stopDragging?.();
          return;
        }

        const distance = move.clientX - startX;

        if (!this.suppressDragClick) {
          if (Math.abs(distance) < 6) return;

          if (Math.abs(move.clientY - startY) > Math.abs(distance)) {
            this.stopDragging?.();
            return;
          }

          this.suppressDragClick = true;
          track.style.setProperty('scroll-behavior', 'auto');
          track.style.setProperty('scroll-snap-type', 'none');
          track.style.setProperty('cursor', 'grabbing');
        }

        move.preventDefault();
        track.scrollLeft = startScroll - distance;
      },
      options
    );

    const finish = (end) => {
      if (end.pointerId === event.pointerId) {
        this.stopDragging?.();
      }
    };

    window.addEventListener('pointerup', finish, options);
    window.addEventListener('pointercancel', finish, options);
    window.addEventListener(
      'blur',
      () => this.stopDragging?.(),
      options
    );
  };

  preventNativeDrag = (event) => {
    if (this.stopDragging) event.preventDefault();
  };

  preventDragClick = (event) => {
    if (!this.suppressDragClick || event.detail === 0) return;

    this.suppressDragClick = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  handleVariantChange = (event) => {
    const select = event.target.closest('[data-upsell-variant]');
    if (!select) return;

    const card = select.closest('.fabric-bundle__card');
    const price = card?.querySelector('[data-upsell-price]');
    const option = select.options[select.selectedIndex];

    if (!price || !option) return;

    const formatter = new Intl.NumberFormat(
      document.documentElement.lang || 'tr-TR',
      {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'TRY',
        maximumFractionDigits:
          Number(option.dataset.price) % 100 === 0 ? 0 : 2,
      }
    );

    price.textContent = formatter.format(
      Number(option.dataset.price) / 100
    );
  };

  handleClick = (event) => {
    const navigationButton = event.target.closest('[data-upsell-scroll]');

    if (navigationButton) {
      this.scrollProducts(navigationButton.dataset.upsellScroll);
      return;
    }

    const button = event.target.closest('[data-upsell-add]');
    if (!button) return;

    this.addVariant(button);
  };

  handleScroll = () => {
    this.updateNavigation();
  };

  handleResize = () => {
    this.updateNavigation();
  };

  scrollProducts(direction) {
    if (!this.track) return;

    const card = this.track.querySelector('.fabric-bundle__card');

    const distance =
      (card?.getBoundingClientRect().width ||
        this.track.clientWidth * 0.88) + 10;

    this.track.scrollBy({
      left: direction === 'next' ? distance : -distance,
      behavior: 'smooth',
    });
  }

  updateNavigation() {
    if (!this.track) return;

    const previousButton = this.querySelector(
      '[data-upsell-scroll="prev"]'
    );
    const nextButton = this.querySelector(
      '[data-upsell-scroll="next"]'
    );

    const hasOverflow =
      this.track.scrollWidth > this.track.clientWidth + 4;

    if (previousButton) {
      previousButton.hidden = !hasOverflow;
      previousButton.disabled = this.track.scrollLeft <= 4;
    }

    if (nextButton) {
      nextButton.hidden = !hasOverflow;
      nextButton.disabled =
        this.track.scrollLeft + this.track.clientWidth >=
        this.track.scrollWidth - 4;
    }
  }

  async addVariant(button) {
    const card = button.closest('.fabric-bundle__card');
    const select = card?.querySelector('[data-upsell-variant]');
    const status = card?.querySelector('[data-upsell-status]');
    const option = select?.options[select.selectedIndex];

    if (!select || !option || option.disabled) return;

    const variantId = String(option.value);
    const previousText = button.textContent;

    button.disabled = true;
    button.textContent = 'Ekleniyor…';

    if (status) status.textContent = '';

    const sectionIds = Array.from(
      document.querySelectorAll(
        'cart-items-component[data-section-id]'
      )
    )
      .map((item) => item.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [
          {
            merchandiseId: variantId,
            quantity: 1,
          },
        ],
        promise: deferred.promise,
      })
    );

    try {
      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                _Kampanya: 'Günlük + Özel Gün',
              },
            },
          ],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(
          result.description ||
            result.message ||
            'Ürün sepete eklenemedi.'
        );
      }

      const cartResponse = await fetch(`${root}cart.js`, {
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!cartResponse.ok) {
        throw new Error('Sepet güncellenemedi.');
      }

      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: 1,
          sections: result.sections,
          source: 'fabric-bundle-upsell',
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);

      if (status) {
        status.textContent =
          error.message ||
          'Bir hata oluştu. Lütfen tekrar deneyin.';
      }

      button.disabled = false;
      button.textContent = previousText;
    }
  }
}

if (!customElements.get('fabric-bundle-upsell')) {
  customElements.define('fabric-bundle-upsell', FabricBundleUpsell);
}